/**
 * useProposalsKanbanRealtime - Realtime centralizado do Kanban de Propostas
 *
 * Um único canal por tenant escuta a tabela `crm_proposals`. Quando a situação
 * muda, o card é removido das colunas antigas (atualização otimista do cache)
 * e a coluna nova + as contagens são invalidadas — os cards andam sozinhos,
 * sem arrastar e soltar.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 1 — Kanban de Propostas)
 */
import { useEffect } from 'react';
import { useQueryClient, InfiniteData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { resolveProposalStatusColumn } from '@/components/crm/proposals/kanban/proposalKanbanColumns';
import type { KanbanProposal } from '@/hooks/useProposalKanbanColumn';
import type { ProposalStatus } from '@/hooks/useDealProposals';

const logger = createLogger('ProposalsKanbanRealtime');

/**
 * Ativa o Realtime do Kanban de Propostas enquanto o board estiver montado.
 */
export function useProposalsKanbanRealtime(enabled: boolean = true) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !currentTenant?.id) return;

    const tenantId = currentTenant.id;

    /** Remove a proposta de todas as colunas em cache, exceto da coluna informada */
    const removeFromOtherColumns = (proposalId: string, keepStatus: ProposalStatus | null) => {
      const queries = queryClient.getQueriesData<InfiniteData<KanbanProposal[]>>({
        queryKey: ['proposal-kanban-column', tenantId],
      });

      queries.forEach(([key, data]) => {
        if (!data) return;
        const status = key[2] as ProposalStatus | undefined;
        if (status && status === keepStatus) return;

        const exists = data.pages.some((page) => page.some((p) => p.id === proposalId));
        if (!exists) return;

        queryClient.setQueryData<InfiniteData<KanbanProposal[]>>(key, {
          ...data,
          pages: data.pages.map((page) => page.filter((p) => p.id !== proposalId)),
        });
      });
    };

    /** Invalida uma coluna específica (para o card vir com os joins completos) */
    const invalidateStatus = (status: ProposalStatus | null) => {
      if (!status) return;
      queryClient.invalidateQueries({
        queryKey: ['proposal-kanban-column', tenantId, status],
      });
    };

    const channel = supabase
      .channel(`proposals-kanban-${tenantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_proposals',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          const newRow = payload.new as { id?: string; status?: string } | null;
          const oldRow = payload.old as { id?: string; status?: string } | null;
          const proposalId = newRow?.id || oldRow?.id;
          if (!proposalId) return;

          const newStatus = resolveProposalStatusColumn(newRow?.status);
          const oldStatus = resolveProposalStatusColumn(oldRow?.status);

          logger.debug('Evento realtime de proposta', {
            event: payload.eventType,
            proposalId,
            oldStatus,
            newStatus,
          });

          if (payload.eventType === 'DELETE') {
            removeFromOtherColumns(proposalId, null);
          } else {
            removeFromOtherColumns(proposalId, newStatus);
            invalidateStatus(newStatus);
          }

          queryClient.invalidateQueries({ queryKey: ['proposal-kanban-counts', tenantId] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled, currentTenant?.id, queryClient]);
}
